import { ReactNode } from "react";

/**
 * SISLessonShell — saffron-and-indigo lesson frame for The South Indian
 * Songbook. The visual language reaches for warm parchment + a single
 * decorative paisley/swara motif rather than the cold dark-mode used by
 * the rock-pop courses. The course is essentially a Carnatic-meets-Western
 * masterclass — Ilaiyaraaja, A.R. Rahman, Harris Jayaraj — so the design
 * tone is closer to "concert hall recital" than "Spotify playlist".
 */

interface SISLessonShellProps {
  title: string;
  composer: string;
  film?: string;
  year?: string;
  /** Western-key analysis (e.g. "B minor"). */
  westernKey?: string;
  /** Carnatic raga / Indian-music label (e.g. "Kamavardhini", "Dorian-flavoured Bm"). */
  raga?: string;
  meter?: string;
  bpm?: number;
  children: ReactNode;
}

export function SISLessonShell({
  title,
  composer,
  film,
  year,
  westernKey,
  raga,
  meter,
  bpm,
  children,
}: SISLessonShellProps) {
  return (
    <div className="min-h-screen pb-16 text-[#2b1d10]" style={{ background: "#fbf2dc" }}>
      {/* Subtle paisley/sun motif — pure CSS gradient, no extra image */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background: `
            radial-gradient(900px 420px at 92% -10%, rgba(217,119,6,0.12), transparent 60%),
            radial-gradient(700px 380px at -10% 110%, rgba(67,56,202,0.08), transparent 60%),
            radial-gradient(380px 200px at 50% 50%, rgba(255,255,255,0.4), transparent 60%)
          `,
        }}
      />

      {/* HERO ----------------------------------------------------------- */}
      <section className="relative overflow-hidden border-b border-amber-700/30 bg-gradient-to-b from-amber-100/60 via-transparent to-transparent">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-10 lg:flex-row lg:items-end lg:justify-between lg:px-8 lg:py-16">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.32em] text-amber-700">
              The South Indian Songbook
            </div>
            <h1
              className="mt-2 text-4xl font-medium leading-tight text-[#1f1408] lg:text-6xl"
              style={{
                fontFamily:
                  '"Cormorant Garamond", "Spectral", "Tiro Tamil", Georgia, serif',
                letterSpacing: "0.005em",
              }}
            >
              {title}
            </h1>
            {(composer || film) && (
              <div className="mt-3 text-base text-amber-900/80">
                <span className="font-medium text-amber-900">— {composer}</span>
                {film && (
                  <span className="ml-2 italic">
                    from <em>{film}</em>
                  </span>
                )}
                {year && <span className="ml-2 text-amber-700">· {year}</span>}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-[12px] font-mono">
            {westernKey && (
              <span className="rounded-full border border-amber-700/40 bg-amber-100/60 px-3 py-1 text-amber-900">
                {westernKey}
              </span>
            )}
            {raga && (
              <span className="rounded-full border border-indigo-700/40 bg-indigo-100/40 px-3 py-1 text-indigo-900">
                Raga · {raga}
              </span>
            )}
            {meter && (
              <span className="rounded-full border border-zinc-500/40 bg-stone-100 px-3 py-1 text-stone-700">
                {meter}
              </span>
            )}
            {bpm && (
              <span className="rounded-full border border-zinc-500/40 bg-stone-100 px-3 py-1 text-stone-700">
                ≈ {bpm} bpm
              </span>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-10 lg:px-8 lg:py-12">{children}</main>
    </div>
  );
}

/* SISCard — replaces the dark `Chapter` for SIS lessons. */
export function SISCard({
  label,
  title,
  children,
}: {
  label?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-amber-700/20 bg-white/70 p-6 shadow-[0_4px_30px_rgba(120,53,15,0.06)] backdrop-blur-sm">
      {(label || title) && (
        <div className="mb-3">
          {label && (
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-indigo-800/80">
              {label}
            </div>
          )}
          {title && (
            <h2
              className="mt-1 text-2xl font-medium text-[#1f1408] lg:text-3xl"
              style={{
                fontFamily:
                  '"Cormorant Garamond", "Spectral", Georgia, serif',
              }}
            >
              {title}
            </h2>
          )}
        </div>
      )}
      <div className="space-y-3 text-[16px] leading-[1.78] text-[#2b1d10]/95">{children}</div>
    </section>
  );
}

/* SISCallout — saffron-tinged inline pull-out for "what to listen for". */
export function SISCallout({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <aside className="my-3 rounded-xl border-l-4 border-amber-700/70 bg-amber-50 px-4 py-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-700">
        {label}
      </div>
      <div className="text-[15px] leading-relaxed text-[#2b1d10]">{children}</div>
    </aside>
  );
}

/* SISInfluence — for "who/what they were listening to" notes. */
export function SISInfluence({
  artists,
  children,
}: {
  artists: string[];
  children: ReactNode;
}) {
  return (
    <aside className="my-3 rounded-xl border border-indigo-700/30 bg-indigo-50/80 p-4">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-indigo-700">
        ☂ Influences worth a listen
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {artists.map((a) => (
          <span
            key={a}
            className="rounded-full border border-indigo-700/30 bg-white px-2.5 py-0.5 text-[11px] font-medium text-indigo-900"
          >
            {a}
          </span>
        ))}
      </div>
      <div className="text-[14.5px] leading-relaxed text-[#1e1b4b]">{children}</div>
    </aside>
  );
}

/* SISTakeaway — emerald, "how this grows you as a musician". */
export function SISTakeaway({ children }: { children: ReactNode }) {
  return (
    <aside className="my-3 rounded-xl border-l-4 border-emerald-700/70 bg-emerald-50 px-4 py-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700">
        ★ How this grows you as a musician
      </div>
      <div className="text-[15px] leading-relaxed text-[#1c2e1c]">{children}</div>
    </aside>
  );
}

/* SISImage wrapper — the inline handwritten note, scaled for the cream theme. */
export function SISNoteImage({
  src,
  caption,
}: {
  src: string;
  caption?: string;
}) {
  return (
    <figure className="my-5 overflow-hidden rounded-2xl border border-amber-700/20 bg-white shadow-[0_4px_30px_rgba(120,53,15,0.08)]">
      <img
        src={src}
        alt={caption ?? "Handwritten chord chart"}
        className="block max-h-[480px] w-auto mx-auto"
        loading="lazy"
      />
      {caption && (
        <figcaption className="border-t border-amber-700/15 bg-amber-50 px-4 py-2 text-[12px] italic text-amber-900">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
